import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Smartphone, Download } from 'lucide-react';
import { Button } from './ui/button';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [headerOpacity, setHeaderOpacity] = useState(0.9);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  // Track scroll position for progress bar and header opacity
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = window.scrollY;

      // Calculate scroll progress (0-100)
      const progress = (scrolled / documentHeight) * 100;
      setScrollProgress(progress);

      // Adjust header opacity based on scroll (more opaque when scrolled)
      const opacity = Math.min(0.9 + (scrolled / 500) * 0.1, 1);
      setHeaderOpacity(opacity);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  return (
    <header role="banner" className="sticky top-0 z-50 w-full">
      {/* Progress Bar */}
      <div
        className="absolute top-0 left-0 h-1 bg-[#f9f506] transition-all duration-300 ease-out"
        style={{ width: `${scrollProgress}%` }}
        role="progressbar"
        aria-valuenow={Math.round(scrollProgress)}
        aria-valuemin="0"
        aria-valuemax="100"
        aria-label="Page scroll progress"
      />

      <div
        className="w-full bg-white backdrop-blur-md border-b border-gray-100 transition-all duration-300"
        style={{ opacity: headerOpacity }}
      >
        <nav
          className="max-w-[1200px] mx-auto px-4 sm:px-10 py-3 flex items-center justify-between"
          aria-label="Main navigation"
        >
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            aria-label="BizzKonnect home"
          >
            <div className="flex items-center justify-center h-8 w-8">
              <img
                src="/logo/logo_bizz.png"
                alt="BizzKonnect Logo"
                className="h-full w-full object-contain"
              />
            </div>
            <span className="text-[#181811] text-xl font-bold leading-none">BizzKonnect</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8 h-10" role="navigation">
            <Link
              to="/features"
              className={`flex items-center h-10 text-sm font-medium transition-colors hover:text-[#f9f506] ${isActive('/features') ? 'text-[#f9f506]' : 'text-[#181811]'
                }`}
              aria-current={isActive('/features') ? 'page' : undefined}
            >
              Features
            </Link>
            <Link
              to="/how-it-works"
              className={`flex items-center h-10 text-sm font-medium transition-colors hover:text-[#f9f506] ${isActive('/how-it-works') ? 'text-[#f9f506]' : 'text-[#181811]'
                }`}
              aria-current={isActive('/how-it-works') ? 'page' : undefined}
            >
              How it Works
            </Link>
            <Link
              to="/testimonials"
              className={`flex items-center h-10 text-sm font-medium transition-colors hover:text-[#f9f506] ${isActive('/testimonials') ? 'text-[#f9f506]' : 'text-[#181811]'
                }`}
              aria-current={isActive('/testimonials') ? 'page' : undefined}
            >
              Testimonials
            </Link>
            <Link
              to="/support"
              className={`flex items-center h-10 text-sm font-medium transition-colors hover:text-[#f9f506] ${isActive('/support') ? 'text-[#f9f506]' : 'text-[#181811]'
                }`}
              aria-current={isActive('/support') ? 'page' : undefined}
            >
              Support
            </Link>
          </div>

          {/* CTA & Mobile Menu Toggle */}
          <div className="flex items-center gap-4">
            {/* Download App Button with Notification Dot */}
            <Button
              className="h-10 px-4 bg-[#181811] text-white hover:bg-black font-bold shadow-sm hidden sm:flex items-center relative"
              asChild
            >
              <a href="#download" className="flex items-center gap-2">
                <Download className="w-4 h-4" aria-hidden="true" />
                <span>Get the App</span>
                {/* Notification dot */}
                <span
                  className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse"
                  aria-label="New"
                />
              </a>
            </Button>

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6" aria-hidden="true" />
              ) : (
                <Menu className="w-6 h-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </nav>

        {/* Mobile Menu with Slide-in Animation */}
        <div
          id="mobile-menu"
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            }`}
          role="navigation"
          aria-label="Mobile navigation"
        >
          <div className="bg-white border-t border-gray-100 animate-slide-in-down">
            <div className="flex flex-col gap-1 p-4">
              <Link
                to="/features"
                className={`px-4 py-3 rounded-lg transition-all hover:bg-gray-50 font-medium ${isActive('/features') ? 'bg-[#f9f506]/10 text-[#181811]' : 'text-[#181811]'
                  }`}
                onClick={() => setIsMenuOpen(false)}
                aria-current={isActive('/features') ? 'page' : undefined}
              >
                Features
              </Link>
              <Link
                to="/how-it-works"
                className={`px-4 py-3 rounded-lg transition-all hover:bg-gray-50 font-medium ${isActive('/how-it-works') ? 'bg-[#f9f506]/10 text-[#181811]' : 'text-[#181811]'
                  }`}
                onClick={() => setIsMenuOpen(false)}
                aria-current={isActive('/how-it-works') ? 'page' : undefined}
              >
                How it Works
              </Link>
              <Link
                to="/testimonials"
                className={`px-4 py-3 rounded-lg transition-all hover:bg-gray-50 font-medium ${isActive('/testimonials') ? 'bg-[#f9f506]/10 text-[#181811]' : 'text-[#181811]'
                  }`}
                onClick={() => setIsMenuOpen(false)}
                aria-current={isActive('/testimonials') ? 'page' : undefined}
              >
                Testimonials
              </Link>
              <Link
                to="/support"
                className={`px-4 py-3 rounded-lg transition-all hover:bg-gray-50 font-medium ${isActive('/support') ? 'bg-[#f9f506]/10 text-[#181811]' : 'text-[#181811]'
                  }`}
                onClick={() => setIsMenuOpen(false)}
                aria-current={isActive('/support') ? 'page' : undefined}
              >
                Support
              </Link>

              {/* Mobile CTA */}
              <Button
                className="bg-[#181811] text-white hover:bg-black font-bold w-full mt-2 relative"
                asChild
              >
                <a href="#download" className="flex items-center justify-center gap-2">
                  <Download className="w-4 h-4" aria-hidden="true" />
                  <span>Get the App</span>
                  <span
                    className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse"
                    aria-label="New"
                  />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};